import { Button } from 'azure-devops-ui/Button';
import { ObservableValue } from 'azure-devops-ui/Core/Observable';
import { Observer } from 'azure-devops-ui/Observer';
import { Location } from 'azure-devops-ui/Utilities/Position';
import {
  ITeachingBubble,
  ITeachingBubbleProps,
  TeachingBubble,
} from 'azure-devops-ui/TeachingBubble';
import React, { createContext, useContext, useEffect } from 'react';
import { TeachingService } from '../services/teach.service';

export const TeachingServiceContext = createContext({} as TeachingService);

export function useTeachingService() {
  return useContext(TeachingServiceContext);
}

const ActiveBubbleCacheKey = 'activeBubble';
const TeachingCompletedCacheKey = 'teachingCompleted';

function getActiveBubbleFromCache(): number {
  if (isTeachingCompleted()) return -1;

  const position = window.localStorage.getItem(ActiveBubbleCacheKey);
  if (position != undefined && position != null) return parseInt(position);
  return -1;
}

function isTeachingCompleted(): boolean {
  return !!window.localStorage.getItem(TeachingCompletedCacheKey);
}

export default function Teach(props: { teachingService: TeachingService; children: any }) {
  const activeBubble = new ObservableValue<number>(getActiveBubbleFromCache());
  const teachingBubbles = new ObservableValue<ITeachingBubbleProps[]>([]);

  function setActiveBubble(position: number) {
    if (isTeachingCompleted()) {
      activeBubble.value = -1;
      return;
    }

    activeBubble.value = position;
    window.localStorage.setItem(ActiveBubbleCacheKey, position.toString());
  }

  function setTeachingCompleted() {
    window.localStorage.setItem(TeachingCompletedCacheKey, '1');
  }

  function next() {
    if (activeBubble.value < teachingBubbles.value.length - 1) {
      setActiveBubble(activeBubble.value + 1);
    }
  }

  function previous() {
    if (activeBubble.value > 0) {
      setActiveBubble(activeBubble.value - 1);
    }
  }

  const nextButton = {
    text: 'Next',
    onClick: next,
  };

  const backButton = {
    text: 'Previous',
    onClick: previous,
  };

  function onTeachingBubblesChanged(newBubbles: ITeachingBubbleProps[]) {
    if (newBubbles.length > 1) {
      newBubbles.slice(1, -1).forEach((x) => {
        x.primaryButtonProps = backButton;
        x.secondaryButtonProps = nextButton;
      });
      newBubbles[0].primaryButtonProps = nextButton;
      newBubbles.at(-1).primaryButtonProps = backButton;
    }

    teachingBubbles.value = newBubbles;
    const latestBubble = getActiveBubbleFromCache();
    if (newBubbles.length > 0) {
      if (latestBubble >= 0) setActiveBubble(latestBubble);
      else setActiveBubble(0);
    }
  }

  function onTeachingDone(position: number) {
    if (activeBubble.value == position) {
      // it's last teaching bubble
      if (position == teachingBubbles.value.length - 1) {
        setActiveBubble(-1);
        setTeachingCompleted();
      } else setActiveBubble(activeBubble.value + 1);
    }
  }

  useEffect(() => {
    props.teachingService.subscribe(onTeachingBubblesChanged, onTeachingDone);

    return () => props.teachingService.unsubscribe(onTeachingBubblesChanged, onTeachingDone);
  }, [props.teachingService]);

  return (
    <TeachingServiceContext.Provider value={props.teachingService}>
      {props.children}
      <Observer teachingBubbles={teachingBubbles}>
        {(bubblesProps: { teachingBubbles: ITeachingBubbleProps[] }) => {
          return (
            <Observer activeBubble={activeBubble}>
              {(props: { activeBubble: number }) => {
                return props.activeBubble !== -1 &&
                  bubblesProps.teachingBubbles.length > props.activeBubble ? (
                  <TeachingBubble
                    {...bubblesProps.teachingBubbles[props.activeBubble]}
                    onDismiss={() => {
                      // const buttonRefs = [
                      //   newWorkItemButtonRef,
                      //   newWorkItemButtonRef,
                      //   openFilteredViewButtonRef,
                      //   columnOptionsButtonRef,
                      // ];
                      // const buttonToFocus = buttonRefs[props.activeBubble];
                      // if (buttonToFocus && buttonToFocus.current) {
                      //   buttonToFocus.current.focus();
                      // }
                      setActiveBubble(-1);
                      setTeachingCompleted();
                    }}
                  />
                ) : null;
              }}
            </Observer>
          );
        }}
      </Observer>
    </TeachingServiceContext.Provider>
  );
}
